import { c } from "react/compiler-runtime";
import { useCache } from "@effektify/cache";
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { QUERY_KEYS } from "../constants.js";
import useLogger from "./useLogger.js";
import fetchData from "../api/fetchData.js";
import { isDataset, isMainReportDataResponse, isDataWithEndpoint, isDataWithDataset } from "../types/src/guards/data.js";
const defaultResponse = {
  data: {
    dataEndpoints: null
  },
  isLoading: false,
  isError: false,
  isPending: false
};
const dedupeAdditionalEndpoints = endpoints => Object.entries(endpoints).reduce((acc, [dataKey, endpoint]) => {
  const hash = `${endpoint.method}-${endpoint.uri}`;
  if (!acc[hash]) acc[hash] = {
    keys: [],
    endpoint
  };
  acc[hash].keys.push(dataKey);
  return acc;
}, {});
const useGetReportData = t0 => {
  const $ = c(55);
  const {
    getToken,
    endpoint,
    shouldFetch,
    params: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === void 0 ? {} : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const params = t2;
  const logger = useLogger("useGetReportData");
  let t3;
  if ($[2] !== getToken) {
    t3 = fetchData(getToken);
    $[2] = getToken;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const dataFetcher = t3;
  const [responseData, setResponseData] = useState(defaultResponse);
  const {
    updateTranslations,
    addSimulatedData,
    updateVariables,
    updateGoals,
    resetDataOnly
  } = useCache();
  let t4;
  if ($[4] !== endpoint) {
    t4 = endpoint || {
      uri: "",
      method: ""
    };
    $[4] = endpoint;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const {
    uri,
    method,
    params: t5
  } = t4;
  let t6;
  if ($[6] !== t5) {
    t6 = t5 === void 0 ? {} : t5;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const defaultParams = t6;
  let t7;
  if ($[8] !== defaultParams || $[9] !== params) {
    t7 = {
      ...defaultParams,
      ...params
    };
    $[8] = defaultParams;
    $[9] = params;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const mergedParams = t7;
  let t8;
  if ($[11] !== mergedParams) {
    t8 = JSON.stringify(mergedParams);
    $[11] = mergedParams;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t8 || $[14] !== uri) {
    t9 = [QUERY_KEYS.REPORT_DATA, uri, t8];
    $[13] = t8;
    $[14] = uri;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== dataFetcher || $[17] !== mergedParams || $[18] !== method || $[19] !== resetDataOnly || $[20] !== uri) {
    t10 = async () => {
      await resetDataOnly();
      return dataFetcher({
        uri,
        method,
        params: mergedParams
      });
    };
    $[16] = dataFetcher;
    $[17] = mergedParams;
    $[18] = method;
    $[19] = resetDataOnly;
    $[20] = uri;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const t11 = !!uri && !!method && !!shouldFetch;
  let t12;
  if ($[22] !== t10 || $[23] !== t11 || $[24] !== t9) {
    t12 = {
      queryKey: t9,
      queryFn: t10,
      staleTime: 9e5,
      enabled: t11
    };
    $[22] = t10;
    $[23] = t11;
    $[24] = t9;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const {
    data: reportData,
    isLoading,
    isError,
    isPending,
    isFetching,
    dataUpdatedAt
  } = useQuery(t12);
  let t13;
  if ($[26] !== addSimulatedData) {
    t13 = newData => {
      Object.entries(newData || {}).forEach(t142 => {
        const [key, value] = t142;
        if (isDataset(value)) {
          addSimulatedData(key, "success", value);
        }
      });
    };
    $[26] = addSimulatedData;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const updateReportData = t13;
  let t14;
  if ($[28] !== logger) {
    t14 = newData_0 => {
      setResponseData(prev => {
        if (prev && JSON.stringify(prev.data) === JSON.stringify(newData_0)) {
          logger.debug("useGetReportData Storing previous report data", prev);
          return prev;
        }
        logger.debug("useGetReportData Updating report data", newData_0);
        return {
          ...newData_0
        };
      });
    };
    $[28] = logger;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const updateResponseData = t14;
  let t15;
  if ($[30] !== isLoading || $[31] !== isPending || $[32] !== logger || $[33] !== reportData || $[34] !== updateGoals || $[35] !== updateReportData || $[36] !== updateResponseData || $[37] !== updateTranslations || $[38] !== updateVariables) {
    t15 = () => {
      let response = defaultResponse;
      if (!reportData && (isLoading || isPending)) {
        response = {
          ...defaultResponse,
          isLoading: true,
          isPending: true
        };
      }
      if (reportData instanceof Error) {
        logger.error("Error fetching and parsing report data:", reportData);
        response = {
          ...defaultResponse,
          data: {
            dataEndpoints: {}
          },
          isError: true
        };
      }
      if (!isMainReportDataResponse(reportData) && !isPending && !isLoading) {
        response = {
          ...defaultResponse,
          data: {
            dataEndpoints: {}
          },
          isError: false,
          isLoading: false,
          isPending: false
        };
      }
      if (isMainReportDataResponse(reportData)) {
        const {
          data,
          meta
        } = reportData;
        const {
          translations: t162,
          variables: t172,
          goals: t18
        } = meta || {};
        const translations = t162 === void 0 ? {} : t162;
        const variables = t172 === void 0 ? {} : t172;
        const goals = t18 === void 0 ? {} : t18;
        updateTranslations(translations);
        updateVariables(variables);
        updateGoals(goals);
        const additionalEndpoints = {};
        const currentReportData = {};
        Object.entries(data).forEach(t19 => {
          const [dataKey, dataObject] = t19;
          if (isDataWithEndpoint(dataObject)) {
            const {
              endpoint: endpoint_0
            } = dataObject;
            additionalEndpoints[dataKey] = endpoint_0;
          }
          if (isDataWithDataset(dataObject)) {
            currentReportData[dataKey] = dataObject;
          }
        });
        const dedupedMap = dedupeAdditionalEndpoints(additionalEndpoints);
        updateReportData(currentReportData);
        response = {
          data: {
            dataEndpoints: dedupedMap
          },
          isLoading: false,
          isError: false,
          isPending: false
        };
      }
      updateResponseData(response);
    };
    $[30] = isLoading;
    $[31] = isPending;
    $[32] = logger;
    $[33] = reportData;
    $[34] = updateGoals;
    $[35] = updateReportData;
    $[36] = updateResponseData;
    $[37] = updateTranslations;
    $[38] = updateVariables;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== dataUpdatedAt || $[41] !== isError || $[42] !== isLoading || $[43] !== isPending || $[44] !== logger || $[45] !== reportData || $[46] !== updateGoals || $[47] !== updateReportData || $[48] !== updateResponseData || $[49] !== updateTranslations || $[50] !== updateVariables) {
    t16 = [reportData, isLoading, isError, isPending, dataUpdatedAt, logger, updateGoals, updateReportData, updateResponseData, updateTranslations, updateVariables];
    $[40] = dataUpdatedAt;
    $[41] = isError;
    $[42] = isLoading;
    $[43] = isPending;
    $[44] = logger;
    $[45] = reportData;
    $[46] = updateGoals;
    $[47] = updateReportData;
    $[48] = updateResponseData;
    $[49] = updateTranslations;
    $[50] = updateVariables;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  useEffect(t15, t16);
  let t17;
  if ($[52] !== isFetching || $[53] !== responseData) {
    t17 = {
      ...responseData,
      isFetching
    };
    $[52] = isFetching;
    $[53] = responseData;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  return t17;
};
export { useGetReportData as default };